import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import {
  Button,
  Card,
  Empty,
  Alert,
  Grid,
  Input,
  List,
  Modal,
  Pagination,
  Select,
  Space,
  Table,
  Tag,
  Tabs,
  Tooltip,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { PATHS } from "../../../app/router/paths";
import { normalizeApiError } from "../../../shared/errors/normalizeApiError";
import { appNotification } from "../../../shared/notifications/appNotification";
import { PageHeader } from "../../../shared/components/PageHeader";
import { MobilePagination } from "../../../shared/components/MobilePagination";
import {
  deletePropose,
  fetchProposes,
  type ProposeListSort,
  type ProposeSalesBucket,
} from "../api/proposesApi";
import {
  deleteProposeTemplateSubmission,
  getProposeSubmissionGoogleSheetStatus,
  getProposeTemplateSubmissionPreview,
  listSellerFinishedProposeTemplateSubmissions,
  listProposeTemplateSubmissions,
  listSellerProposeTemplateSubmissions,
  updateProposeTemplateSubmissionCells,
  type ProposeTemplateCellEdit,
  type ProposeTemplatePreview,
} from "../api/proposeTemplatesApi";
import type { PaginationMeta } from "../../../shared/api/pagination";
import type { Propose } from "../model/types";
import { formatRepresentativeId } from "../model/proposeEditor";
import { statusColor, statusLabel } from "../model/proposeStatus";
import { useAuth } from "../../auth/model/AuthContext";
import { usePageRefresh } from "../../../shared/pull-refresh/PullRefreshContext";
import { TemplateSheetPreview } from "./ProposeTemplateManagementPage";
import "../styles/ProposeList.css";

const PAGE_SIZE = 10;
const SUBMISSION_PAGE_LIMIT = 50;
const { useBreakpoint } = Grid;
type SaleProposalRow = {
  proposal: Propose;
  urgency?: string;
  hasActiveSubmission?: boolean;
};
type TemplateSubmissionRow = Record<string, unknown>;

const submissionRowsFromResponse = (response: unknown): TemplateSubmissionRow[] => {
  if (Array.isArray(response)) {
    return response.filter(
      (item): item is TemplateSubmissionRow => Boolean(item && typeof item === "object"),
    );
  }
  if (!response || typeof response !== "object") return [];
  const record = response as Record<string, unknown>;
  if ("id" in record || "order_name" in record || "template_name" in record) {
    return [record];
  }
  for (const key of ["items", "submissions", "results", "data"]) {
    const nested = record[key];
    if (Array.isArray(nested)) return submissionRowsFromResponse(nested);
    if (nested && typeof nested === "object") {
      const rows = submissionRowsFromResponse(nested);
      if (rows.length) return rows;
    }
  }
  return [];
};

const submissionHasMoreFromResponse = (response: unknown): boolean =>
  Boolean(
    response &&
      typeof response === "object" &&
      (response as Record<string, unknown>).has_more === true,
  );

const submissionValue = (value: unknown) => {
  if (value === undefined || value === null || value === "") return "—";
  if (Array.isArray(value)) return value.map(String).join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};
const displayDate = (value?: string | null) =>
  value ? dayjs(value).format("YYYY/MM/DD") : "—";
const displayDateTime = (value?: string | null) =>
  value ? dayjs(value).format("YYYY/MM/DD HH:mm") : "—";
const deadlineRemaining = (value: string | null | undefined, now: number) => {
  if (!value) return "—";
  const remainingMilliseconds = dayjs(value).diff(now);
  if (remainingMilliseconds <= 0) return "期限切れ";
  if (remainingMilliseconds < 60_000) return "1分未満";
  const remainingMinutes = Math.floor(remainingMilliseconds / 60_000);
  const days = Math.floor(remainingMinutes / (24 * 60));
  const hours = Math.floor((remainingMinutes % (24 * 60)) / 60);
  const minutes = remainingMinutes % 60;
  if (days) return `${days}日 ${hours}時間`;
  if (hours) return `${hours}時間 ${minutes}分`;
  return `${minutes}分`;
};
const hasActiveInputWindow = (proposal: Propose, now: number) =>
  proposal.status === "INPUTTING" &&
  Boolean(proposal.expires_at) &&
  dayjs(proposal.expires_at).isAfter(now);
const deadlineUrgency = (proposal: Propose, now: number) => {
  if (!hasActiveInputWindow(proposal, now)) return undefined;
  const remaining = dayjs(proposal.expires_at).diff(now);
  if (remaining < 60 * 60 * 1000) return "propose-deadline-critical";
  if (remaining < 3 * 60 * 60 * 1000) return "propose-deadline-warning";
  return undefined;
};
const statusOptions = [
  { value: "DRAFT", label: "下書き" },
  { value: "INPUTTING", label: "入力中" },
  { value: "REVIEW", label: "確認待ち" },
  { value: "APPROVED", label: "承認済み" },
  { value: "REJECTED", label: "却下" },
  { value: "SENT", label: "送信済み" },
];

export const ProposeListPage = () => {
  const registerPageRefresh = usePageRefresh();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isCoordinator = Boolean(
    user?.permissions.includes("propose.coordinator.manage"),
  );
  const isSale = Boolean(user?.roles.includes("SALE"));
  const isSellerSubmissionPage = isSale && !isCoordinator;
  // Coordinators use the recommend submissions list from either entry point;
  // the legacy proposal list endpoint is no longer used.
  const isSubmissionPage =
    isCoordinator &&
    (location.pathname === PATHS.PROPOSES.CREATE ||
      location.pathname === PATHS.PROPOSES.ROOT);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [proposes, setProposes] = useState<Propose[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta>();
  const [coordinatorStatus, setCoordinatorStatus] =
    useState<Propose["status"]>();
  const [salesHistoryStatus, setSalesHistoryStatus] =
    useState<Propose["status"]>();
  const [sort, setSort] = useState<ProposeListSort>();
  const [salesBucket, setSalesBucket] = useState<ProposeSalesBucket>("active");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [submissions, setSubmissions] = useState<TemplateSubmissionRow[]>([]);
  const googleSheetUrls = useRef(new Map<number, string>());
  const [submissionsOffset, setSubmissionsOffset] = useState(0);
  const [submissionsHasMore, setSubmissionsHasMore] = useState(false);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [submissionsError, setSubmissionsError] = useState<string | null>(null);
  const [submissionPreview, setSubmissionPreview] =
    useState<TemplateSubmissionRow | null>(null);
  const [templatePreview, setTemplatePreview] =
    useState<ProposeTemplatePreview | null>(null);
  const [submissionPreviewOpen, setSubmissionPreviewOpen] = useState(false);
  const [submissionPreviewLoading, setSubmissionPreviewLoading] = useState(false);
  const [submissionCellEditMode, setSubmissionCellEditMode] = useState(false);
  const [submissionCellSaving, setSubmissionCellSaving] = useState(false);
  const [submissionCellEdits, setSubmissionCellEdits] = useState<
    Record<string, ProposeTemplateCellEdit>
  >({});
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  const appliedSalesBucket = isCoordinator ? undefined : salesBucket;
  const appliedStatus = isCoordinator
    ? coordinatorStatus
    : salesBucket === "history"
      ? salesHistoryStatus
      : undefined;
  const saleProposals = useMemo(
    () =>
      proposes.map((proposal) => ({
        proposal,
        urgency:
          appliedSalesBucket === "active"
            ? deadlineUrgency(proposal, now)
            : undefined,
        hasActiveSubmission:
          proposal.has_sale_submission && hasActiveInputWindow(proposal, now),
      })),
    [appliedSalesBucket, now, proposes],
  );

  const loadProposals = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchProposes({
        page,
        pageSize,
        status: appliedStatus,
        search,
        sort,
        salesBucket: appliedSalesBucket,
      });
      setProposes(result.items);
      setPagination(result.pagination);
    } catch (error) {
      appNotification.error({ message: normalizeApiError(error).message });
    } finally {
      setLoading(false);
    }
  }, [appliedSalesBucket, appliedStatus, page, pageSize, search, sort]);

  useEffect(() => {
    if (isSubmissionPage || isSellerSubmissionPage) return;
    void loadProposals();
  }, [isSellerSubmissionPage, isSubmissionPage, loadProposals]);

  useEffect(() => {
    const loadCoordinatorSubmissions = isSubmissionPage && isCoordinator;
    const loadSaleSubmissions = isSellerSubmissionPage;
    if (!loadCoordinatorSubmissions && !loadSaleSubmissions) return;
    if (isSellerSubmissionPage && !user?.user_id) return;
    let active = true;
    setSubmissionsLoading(true);
    setSubmissionsError(null);
    const request = isSellerSubmissionPage
      ? salesBucket === "history"
        ? listSellerFinishedProposeTemplateSubmissions(
            String(user!.user_id),
            SUBMISSION_PAGE_LIMIT,
            submissionsOffset,
          )
        : listSellerProposeTemplateSubmissions(
            String(user!.user_id),
            SUBMISSION_PAGE_LIMIT,
            submissionsOffset,
          )
      : listProposeTemplateSubmissions();
    request
      .then((response) => {
        if (!active) return;
        const rows = submissionRowsFromResponse(response).map((row) => {
          const id = Number(row.id ?? row.submission_id);
          const readyUrl = googleSheetUrls.current.get(id);
          return readyUrl ? { ...row, url: readyUrl } : row;
        });
        setSubmissions((current) =>
          submissionsOffset === 0 ? rows : [...current, ...rows],
        );
        setSubmissionsHasMore(submissionHasMoreFromResponse(response));
      })
      .catch((error) => {
        if (active) setSubmissionsError(normalizeApiError(error).message);
      })
      .finally(() => {
        if (active) setSubmissionsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [
    isSubmissionPage,
    isCoordinator,
    isSellerSubmissionPage,
    salesBucket,
    submissionsOffset,
    user,
    user?.user_id,
  ]);

  useEffect(() => {
    const submissionId = Number(
      (location.state as { googleSheetSubmissionId?: number } | null)
        ?.googleSheetSubmissionId,
    );
    if (!isSubmissionPage || !isCoordinator || !Number.isFinite(submissionId) || submissionId <= 0) {
      return;
    }

    let active = true;
    const poll = async () => {
      for (let attempt = 0; active && attempt < 120; attempt += 1) {
        try {
          const result = await getProposeSubmissionGoogleSheetStatus(submissionId);
          if (!active) return;
          if (result.success && result.status === "finished" && result.url) {
            googleSheetUrls.current.set(submissionId, result.url);
            setSubmissions((current) => current.map((row) =>
              Number(row.id ?? row.submission_id) === submissionId
                ? { ...row, url: result.url }
                : row,
            ));
            return;
          }
          if (result.status !== "queued" && result.status !== "processing") return;
        } catch {
          // Keep polling transient failures; the submission list remains usable.
        }
        await new Promise<void>((resolve) => window.setTimeout(resolve, 2000));
      }
    };
    void poll();
    return () => {
      active = false;
    };
  }, [isCoordinator, isSubmissionPage, location.state]);
  useEffect(() => {
    registerPageRefresh({ enabled: true, refresh: loadProposals });
    return () => registerPageRefresh(null);
  }, [registerPageRefresh, loadProposals]);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 400);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const goToDetail = (proposal: Propose) =>
    navigate(`${PATHS.PROPOSES.VIEW}?id=${proposal.propose_id}`);
  const goToEdit = (proposal: Propose) =>
    navigate(`${PATHS.PROPOSES.EDIT}?id=${proposal.propose_id}`);
  const goToSalesEdit = (proposal: Propose) =>
    navigate(`${PATHS.PROPOSES.VIEW}?id=${proposal.propose_id}&mode=edit`);
  const remove = async (proposal: Propose) => {
    try {
      await deletePropose(proposal.propose_id);
      appNotification.success({ message: "提案書を削除しました" });
      if (proposes.length === 1 && page > 1) setPage(page - 1);
      setProposes((current) =>
        current.filter((item) => item.propose_id !== proposal.propose_id),
      );
    } catch (error) {
      appNotification.error({ message: normalizeApiError(error).message });
    }
  };
  const confirmRemove = (proposal: Propose) => {
    Modal.confirm({
      title: "この提案書を削除しますか？",
      content: "削除後は復元できず、一覧にも表示されません。",
      okText: "削除",
      cancelText: "キャンセル",
      okButtonProps: { danger: true },
      onOk: () => remove(proposal),
    });
  };
  const previewTemplateSubmission = async (
    submission: TemplateSubmissionRow,
    editMode = false,
  ) => {
    const submissionId = Number(submission.id ?? submission.submission_id);
    if (!Number.isFinite(submissionId)) return;
    setSubmissionPreview(submission);
    setTemplatePreview(null);
    setSubmissionCellEdits({});
    setSubmissionCellEditMode(false);
    setSubmissionPreviewOpen(true);
    setSubmissionPreviewLoading(true);
    try {
      const preview = await getProposeTemplateSubmissionPreview(submissionId);
      setTemplatePreview(preview);
      setSubmissionCellEditMode(editMode);
    } catch (error) {
      appNotification.error({ message: normalizeApiError(error).message });
    } finally {
      setSubmissionPreviewLoading(false);
    }
  };
  const handleSubmissionCellChange = (
    sheetName: string,
    address: string,
    value: string,
  ) => {
    const sheet = templatePreview?.sheets.find((item) => item.name === sheetName);
    const original = String(sheet?.cells[address] ?? "");
    const key = `${sheetName}!${address}`;
    setSubmissionCellEdits((current) => {
      if (value === original) {
        if (!current[key]) return current;
        const next = { ...current };
        delete next[key];
        return next;
      }
      return { ...current, [key]: { sheetName, address, value } };
    });
  };
  const saveSubmissionCellEdits = async () => {
    const submissionId = Number(
      submissionPreview?.id ?? submissionPreview?.submission_id,
    );
    if (!Number.isFinite(submissionId) || !Object.keys(submissionCellEdits).length) {
      return;
    }
    setSubmissionCellSaving(true);
    try {
      const updated = await updateProposeTemplateSubmissionCells(
        submissionId,
        Object.values(submissionCellEdits),
      );
      setTemplatePreview(updated);
      setSubmissionCellEdits({});
      setSubmissionCellEditMode(false);
      setSubmissions((current) => current.map((item) =>
        Number(item.id ?? item.submission_id) === submissionId
          ? { ...item, status: "edited" }
          : item,
      ));
      appNotification.success({ message: "プレビューの編集を保存しました" });
    } catch (error) {
      appNotification.error({ message: normalizeApiError(error).message });
    } finally {
      setSubmissionCellSaving(false);
    }
  };
  const removeTemplateSubmission = (submission: TemplateSubmissionRow) => {
    const submissionId = Number(submission.id ?? submission.submission_id);
    if (!Number.isFinite(submissionId)) return;
    Modal.confirm({
      title: "提出データを削除しますか？",
      okText: "削除",
      cancelText: "キャンセル",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteProposeTemplateSubmission(submissionId);
          setSubmissions((current) =>
            current.filter(
              (item) => Number(item.id ?? item.submission_id) !== submissionId,
            ),
          );
          appNotification.success({ message: "下書きを削除しました" });
        } catch (error) {
          appNotification.error({ message: normalizeApiError(error).message });
        }
      },
    });
  };
  const actionButtons = (proposal: Propose) => (
    <Space
      className="propose-list-actions"
      size={4}
      onClick={(event) => event.stopPropagation()}
    >
      <Tooltip title="詳細を表示">
        <Button
          shape="circle"
          icon={<EyeOutlined />}
          aria-label="詳細を表示"
          onClick={() => goToDetail(proposal)}
        />
      </Tooltip>
      <Tooltip
        title={
          proposal.status === "DRAFT"
            ? "編集"
            : "下書きの提案書のみ編集できます"
        }
      >
        <Button
          type="primary"
          shape="circle"
          icon={<EditOutlined />}
          aria-label="編集"
          disabled={proposal.status !== "DRAFT"}
          onClick={() => goToEdit(proposal)}
        />
      </Tooltip>
      <Tooltip
        title={
          proposal.status === "DRAFT"
            ? "削除"
            : "下書きの提案書のみ削除できます"
        }
      >
        <span>
          <Button
            danger
            shape="circle"
            icon={<DeleteOutlined />}
            aria-label="削除"
            disabled={proposal.status !== "DRAFT"}
            onClick={() => confirmRemove(proposal)}
          />
        </span>
      </Tooltip>
    </Space>
  );
  const mobileActionButtons = (proposal: Propose) => (
    <div
      className="propose-mobile-card-actions"
      onClick={(event) => event.stopPropagation()}
    >
      <Button
        type="default"
        icon={<EyeOutlined />}
        onClick={() => goToDetail(proposal)}
      >
        詳細
      </Button>
      <Button
        type="primary"
        icon={<EditOutlined />}
        disabled={proposal.status !== "DRAFT"}
        onClick={() => goToEdit(proposal)}
      >
        編集
      </Button>
      <Button
        danger
        icon={<DeleteOutlined />}
        disabled={proposal.status !== "DRAFT"}
        onClick={() => confirmRemove(proposal)}
      >
        削除
      </Button>
    </div>
  );
  const saleProposalCard = ({
    proposal,
    urgency,
    hasActiveSubmission,
  }: SaleProposalRow) => (
    <article
      className={`propose-mobile-card propose-mobile-proposal-card ${urgency || ""} ${hasActiveSubmission ? "propose-has-sale-submission" : ""}`}
      onClick={() => goToDetail(proposal)}
    >
      <div className="propose-mobile-card-header">
        <strong className="propose-link">{proposal.propose_code}</strong>
        <Tag color={statusColor(proposal.status)}>
          {statusLabel(proposal.status)}
        </Tag>
      </div>
      <div className="propose-sale-mobile-company">
        <span>会社名</span>
        <strong>{proposal.company_name || "—"}</strong>
        <small>
          買受人コード：{formatRepresentativeId(proposal.representative_id)}
        </small>
      </div>
      <div className="propose-sale-mobile-details">
        <div>
          <span>買受人担当者名</span>
          <strong>{proposal.recipient_name || "—"}</strong>
        </div>
        <div>
          <span>提案期間</span>
          <strong>
            {displayDate(proposal.effective_from)} ～{" "}
            {displayDate(proposal.effective_to)}
          </strong>
        </div>
      </div>
      <div className="propose-sale-mobile-deadline">
        <div>
          <span>営業入力期限</span>
          <strong>{displayDateTime(proposal.expires_at)}</strong>
        </div>
        <div>
          <span>期限まで</span>
          <strong>{deadlineRemaining(proposal.expires_at, now)}</strong>
        </div>
      </div>
      <div
        className={`propose-mobile-card-actions ${proposal.can_sale_edit && hasActiveInputWindow(proposal, now) ? "propose-sale-card-actions--editable" : ""}`}
        onClick={(event) => event.stopPropagation()}
      >
        <Button icon={<EyeOutlined />} onClick={() => goToDetail(proposal)}>
          表示
        </Button>
        {proposal.can_sale_edit && hasActiveInputWindow(proposal, now) && (
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => goToSalesEdit(proposal)}
          >
            入力を編集
          </Button>
        )}
      </div>
    </article>
  );
  const sellerSubmissionCard = (submission: TemplateSubmissionRow) => {
    const information = submission.submission_information as
      | Record<string, unknown>
      | undefined;
    const deadline = information?.deadline
      ? String(information.deadline)
      : undefined;
    const submissionId = Number(submission.id ?? submission.submission_id);
    const statusText = submission.status === "inited"
      ? "入力待ち"
      : submission.status === "edited"
        ? "編集中"
        : submission.status === "finished"
          ? "提出済み"
          : submission.status === 1
            ? "公開中"
            : submission.status === 0
              ? "下書き"
              : submissionValue(submission.status);
    const statusColor = submission.status === "finished" || submission.status === 1
      ? "success"
      : submission.status === "edited"
        ? "processing"
        : undefined;
    return (
      <article
        className={`propose-mobile-card propose-mobile-proposal-card ${submission.is_leader === true || salesBucket === "history" ? "" : "propose-mobile-proposal-card--locked"}`}
        onClick={() => {
          if ((submission.is_leader === true || salesBucket === "history") && Number.isFinite(submissionId)) {
            navigate(`${PATHS.PROPOSES.VIEW}?id=${submissionId}`, {
              state: {
                recommendSubmission: true,
                finishedSubmission: salesBucket === "history",
              },
            });
          }
        }}
      >
        <div className="propose-mobile-card-header">
          <strong className="propose-link">
            {submissionValue(submission.order_name)}
          </strong>
          <Tag color={statusColor}>
            {statusText}
          </Tag>
        </div>
        <div className="propose-sale-mobile-company">
          <span>テンプレート名</span>
          <strong>{submissionValue(submission.template_name)}</strong>
          <small>
            買受人コード：{submissionValue(information?.representative_id)}
          </small>
        </div>
        <div className="propose-sale-mobile-details">
          <div>
            <span>買受人担当者名</span>
            <strong>{submissionValue(information?.representative_name)}</strong>
          </div>
          <div>
            <span>更新日時</span>
            <strong>
              {submission.updated_at
                ? displayDateTime(String(submission.updated_at))
                : "—"}
            </strong>
          </div>
        </div>
        <div className="propose-sale-mobile-deadline">
          <div>
            <span>営業入力期限</span>
            <strong>{deadline ? displayDateTime(deadline) : "—"}</strong>
          </div>
        </div>
        <div
          className={`propose-mobile-card-actions ${submission.is_leader === true ? "propose-sale-card-actions--editable" : ""}`}
        >
          <Button
            icon={<EyeOutlined />}
              onClick={(event) => {
                event.stopPropagation();
                void previewTemplateSubmission(submission);
              }}
          >
            プレビュー
          </Button>
          {submission.is_leader === true && submission.status !== "finished" && salesBucket !== "history" && (
            <Button
              type="primary"
              icon={<EditOutlined />}
                onClick={(event) => {
                  event.stopPropagation();
                  const submissionId = Number(submission.id ?? submission.submission_id);
                  if (Number.isFinite(submissionId)) {
                    navigate(`${PATHS.PROPOSES.VIEW}?id=${submissionId}`, {
                      state: { recommendSubmission: true, editProducts: true },
                    });
                  }
                }}
            >
              入力を編集
            </Button>
          )}
        </div>
      </article>
    );
  };

  if (isSubmissionPage && isCoordinator)
    return (
      <main className="propose-page">
        <PageHeader
          title="提案作成"
          description="テンプレートから作成した注文を確認します"
          actionLabel="新規提案を作成"
          onAction={() => navigate(PATHS.PROPOSES.NEW)}
        />
        <Card className="deadline-list-card propose-list-card" bordered={false}>
          {submissionsError && (
            <Alert
              type="error"
              showIcon
              message="提出一覧を読み込めませんでした"
              description={submissionsError}
              style={{ marginBottom: 16 }}
            />
          )}
          <Table<TemplateSubmissionRow>
            rowKey={(row, index) =>
              String(row.submission_id ?? row.id ?? index ?? "submission")
            }
            loading={submissionsLoading}
            dataSource={submissions}
            locale={{
              emptyText: <Empty description="提出データがありません" />,
            }}
            scroll={{ x: 900 }}
            columns={[
              {
                title: "提案書名",
                dataIndex: "order_name",
                render: submissionValue,
              },
              {
                title: "テンプレート名",
                dataIndex: "template_name",
                render: submissionValue,
              },
              {
                title: "買受人コード",
                render: (_, row) =>
                  submissionValue(
                    row.representative_id ?? row.buyer_code ?? row.customer_code,
                  ),
              },
              {
                title: "買受人担当者名",
                render: (_, row) =>
                  submissionValue(
                    row.representative_name ?? row.recipient_name ?? row.buyer_contact_name ?? row.recipient_id,
                  ),
              },
              {
                title: "営業入力期限",
                render: (_, row) => {
                  const value = row.deadline ?? row.expires_at ?? row.sales_input_deadline;
                  return value ? displayDateTime(String(value)) : "—";
                },
              },
              {
                title: "状態",
                dataIndex: "status",
                render: (value) => {
                  if (value === "inited") return <Tag>入力待ち</Tag>;
                  if (value === "edited") return <Tag color="processing">編集中</Tag>;
                  if (value === "finished") return <Tag color="success">提出済み</Tag>;
                  if (value === 1) return <Tag color="success">1</Tag>;
                  if (value === 0) return <Tag>0</Tag>;
                  return submissionValue(value);
                },
              },
              {
                title: "更新日時",
                dataIndex: "updated_at",
                render: (value) =>
                  value ? displayDateTime(String(value)) : "—",
              },
              {
                title: "操作",
                render: (_, row) => (
                  <Space>
                    <Button
                      shape="circle"
                      icon={<EyeOutlined />}
                      aria-label="プレビュー"
                      title="プレビュー"
                      disabled={typeof row.url !== "string" || !row.url}
                      onClick={() => {
                        if (typeof row.url === "string" && row.url) {
                          window.open(row.url, "_blank", "noopener,noreferrer");
                        }
                      }}
                    />
                    <Button
                      danger
                      shape="circle"
                      icon={<DeleteOutlined />}
                      aria-label="提出データを削除"
                      title="提出データを削除"
                      onClick={() => removeTemplateSubmission(row)}
                    />
                  </Space>
                ),
              },
            ]}
          />
        </Card>
        <Modal
          title={`テンプレートプレビュー：${templatePreview?.name ?? submissionPreview?.template_name ?? ""}`}
          open={submissionPreviewOpen}
          width="96vw"
          style={{ top: 20, maxWidth: 1200 }}
          onCancel={() => {
            setSubmissionPreviewOpen(false);
            setSubmissionPreview(null);
            setTemplatePreview(null);
            setSubmissionCellEditMode(false);
            setSubmissionCellEdits({});
          }}
          footer={
            <Space>
              <Button
                onClick={() => {
                  setSubmissionPreviewOpen(false);
                  setSubmissionPreview(null);
                  setTemplatePreview(null);
                  setSubmissionCellEditMode(false);
                  setSubmissionCellEdits({});
                }}
              >
                閉じる
              </Button>
              {submissionPreview?.status === 0 && (
                submissionCellEditMode ? (
                  <>
                    <Button
                      onClick={() => {
                        setSubmissionCellEditMode(false);
                        setSubmissionCellEdits({});
                      }}
                    >
                      編集をキャンセル
                    </Button>
                    <Button
                      type="primary"
                      loading={submissionCellSaving}
                      disabled={!Object.keys(submissionCellEdits).length}
                      onClick={() => void saveSubmissionCellEdits()}
                    >
                      変更を保存
                    </Button>
                  </>
                ) : (
                  <Button
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={() => setSubmissionCellEditMode(true)}
                  >
                    編集
                  </Button>
                )
              )}
            </Space>
          }
        >
          {submissionPreviewLoading ? (
            <div style={{ textAlign: "center", padding: 24 }}>読み込み中...</div>
          ) : templatePreview ? (
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <Alert
                type="info"
                showIcon
                message="この提出データに使われるテンプレートのExcelプレビューです"
              />
              {templatePreview.sheets.map((sheet) => (
                <Card key={sheet.name} size="small" title={sheet.name}>
                  <TemplateSheetPreview
                    sheet={sheet}
                    edits={submissionCellEdits}
                    onCellChange={handleSubmissionCellChange}
                    readOnly={!submissionCellEditMode}
                  />
                </Card>
              ))}
            </Space>
          ) : null}
        </Modal>
      </main>
    );

  if (!isCoordinator)
    return (
      <main className="propose-page">
        <PageHeader
          title="販売提案"
          description="担当商品の入力依頼"
          actionLabel=""
          onAction={() => undefined}
          isbutton={false}
        />
        <Card className="deadline-list-card propose-list-card" bordered={false}>
          {!isSellerSubmissionPage && (
            <div className="propose-sale-list-filters">
            <Input
              size="large"
              prefix={<SearchOutlined />}
              allowClear
              placeholder="提案番号・買受人コード・会社名・買受人担当者名で検索"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
            {salesBucket === "history" && (
              <Select
                size="large"
                value={salesHistoryStatus}
                allowClear
                placeholder="すべての状態"
                options={statusOptions.filter(
                  (option) => option.value !== "DRAFT",
                )}
                onChange={(value) => {
                  setPage(1);
                  setSalesHistoryStatus(value);
                }}
              />
            )}
            <Select
              size="large"
              value={sort}
              allowClear
              placeholder="入力期限で並び替え"
              options={[
                { value: "expires_at_asc", label: "入力期限が近い順" },
                { value: "expires_at_desc", label: "入力期限が遠い順" },
              ]}
              onChange={(value) => {
                setPage(1);
                setSort(value);
              }}
            />
            </div>
          )}
          <Tabs
            className="propose-sale-deadline-tabs"
            activeKey={salesBucket}
            onChange={(value) => {
              setPage(1);
              setSubmissionsOffset(0);
              setSubmissions([]);
              setSubmissionsHasMore(false);
              setSalesBucket(value as ProposeSalesBucket);
            }}
            items={[
              { key: "active", label: "新しい提案" },
              { key: "history", label: "過去の提案" },
            ]}
          />
          {isSellerSubmissionPage ? (
            <div className="propose-sale-desktop-list">
              {submissionsError && (
                <Alert
                  type="error"
                  showIcon
                  message={salesBucket === "history" ? "過去の提案を読み込めませんでした" : "新しい提案を読み込めませんでした"}
                  description={submissionsError}
                  style={{ marginBottom: 16 }}
                />
              )}
              <List
                className="propose-sale-card-list"
                loading={submissionsLoading}
                dataSource={submissions}
                locale={{
                  emptyText: (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={salesBucket === "history" ? "過去の提案がありません" : "新しい提案がありません"}
                    />
                  ),
                }}
                renderItem={(submission) => (
                  <List.Item className="propose-sale-card-list-item">
                    {sellerSubmissionCard(submission)}
                  </List.Item>
                )}
              />
              {submissionsHasMore && (
                <div className="propose-submissions-load-more">
                  <Button
                    loading={submissionsLoading}
                    onClick={() =>
                      setSubmissionsOffset((offset) => offset + SUBMISSION_PAGE_LIMIT)
                    }
                  >
                    さらに読み込む
                  </Button>
                </div>
              )}
            </div>
          ) : isMobile ? (
            <div className="propose-mobile-list">
              <List
                loading={loading}
                dataSource={saleProposals}
                locale={{
                  emptyText: (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="提案書がありません"
                    />
                  ),
                }}
                renderItem={(saleProposal) => (
                  <List.Item className="propose-mobile-list-item">
                    {saleProposalCard(saleProposal)}
                  </List.Item>
                )}
              />
              <MobilePagination
                current={page}
                pageSize={pageSize}
                total={pagination?.total || 0}
                onChange={setPage}
              />
            </div>
          ) : (
            <div className="propose-sale-desktop-list">
              <List
                className="propose-sale-card-list"
                loading={loading}
                dataSource={saleProposals}
                locale={{
                  emptyText: (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="提案書がありません"
                    />
                  ),
                }}
                renderItem={(saleProposal) => (
                  <List.Item className="propose-sale-card-list-item">
                    {saleProposalCard(saleProposal)}
                  </List.Item>
                )}
              />
              <Pagination
                current={page}
                pageSize={pageSize}
                total={pagination?.total || 0}
                size="middle"
                showSizeChanger
                pageSizeOptions={[10, 20, 50, 100]}
                showTotal={(total, range) =>
                  `${total} 件中 ${range[0]}-${range[1]} を表示`
                }
                onChange={(nextPage, nextPageSize) => {
                  setPage(nextPageSize !== pageSize ? 1 : nextPage);
                  setPageSize(nextPageSize);
                }}
                style={{marginTop: 60}}
              />
            </div>
          )}
        </Card>
        {isSellerSubmissionPage && (
          <Modal
            title={`注文プレビュー：${templatePreview?.name ?? submissionPreview?.template_name ?? ""}`}
            open={submissionPreviewOpen}
            width="96vw"
            style={{ top: 20, maxWidth: 1200 }}
            onCancel={() => {
              setSubmissionPreviewOpen(false);
              setSubmissionPreview(null);
              setTemplatePreview(null);
            }}
            footer={
              <Space>
                <Button
                  onClick={() => {
                    setSubmissionPreviewOpen(false);
                    setSubmissionPreview(null);
                    setTemplatePreview(null);
                    setSubmissionCellEditMode(false);
                    setSubmissionCellEdits({});
                  }}
                >
                  閉じる
                </Button>
                {submissionPreview?.is_leader === true && submissionPreview?.status !== "finished" &&
                  (submissionCellEditMode ? (
                    <>
                      <Button
                        onClick={() => {
                          setSubmissionCellEditMode(false);
                          setSubmissionCellEdits({});
                        }}
                      >
                        編集をキャンセル
                      </Button>
                      <Button
                        type="primary"
                        loading={submissionCellSaving}
                        disabled={!Object.keys(submissionCellEdits).length}
                        onClick={() => void saveSubmissionCellEdits()}
                      >
                        変更を保存
                      </Button>
                    </>
                  ) : (
                    <Button
                      type="primary"
                      icon={<EditOutlined />}
                      onClick={() => setSubmissionCellEditMode(true)}
                    >
                      入力を編集
                    </Button>
                  ))}
              </Space>
            }
          >
            {submissionPreviewLoading ? (
              <div style={{ textAlign: "center", padding: 24 }}>
                読み込み中...
              </div>
            ) : templatePreview ? (
              <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                <Alert
                  type="info"
                  showIcon
                  message="注文に使われるExcelプレビューです"
                />
                {templatePreview.sheets.map((sheet) => (
                  <Card key={sheet.name} size="small" title={sheet.name}>
                    <TemplateSheetPreview
                      sheet={sheet}
                      edits={submissionCellEdits}
                      onCellChange={handleSubmissionCellChange}
                      readOnly={!submissionCellEditMode}
                    />
                  </Card>
                ))}
              </Space>
            ) : null}
          </Modal>
        )}
      </main>
    );

  return (
    <main className="propose-page">
      <PageHeader
        title="販売提案管理"
        description="販売提案書を作成・管理します"
        actionLabel="新規提案を作成"
        onAction={() => navigate(PATHS.PROPOSES.NEW)}
      />
      <Card className="deadline-list-card propose-list-card" bordered={false}>
        <div
          className={
            isMobile ? "propose-list-toolbar-mb" : "propose-list-toolbar-pc"
          }
        >
          <Input
            size="large"
            prefix={<SearchOutlined />}
            allowClear
            placeholder="提案番号・買受人コード・会社名・買受人担当者名で検索"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
          <Select
            value={coordinatorStatus}
            size="large"
            allowClear
            placeholder="すべての状態"
            options={statusOptions}
            onChange={(value) => {
              setPage(1);
              setCoordinatorStatus(value);
            }}
          />
          <Select
            value={sort}
            size="large"
            allowClear
            placeholder="入力期限で並び替え"
            options={[
              { value: "expires_at_asc", label: "入力期限が近い順" },
              { value: "expires_at_desc", label: "入力期限が遠い順" },
            ]}
            onChange={(value) => {
              setPage(1);
              setSort(value);
            }}
          />
        </div>
        {isMobile ? (
          <div className="propose-mobile-list">
            <List
              loading={loading}
              dataSource={proposes}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="提案書がありません"
                  />
                ),
              }}
              renderItem={(proposal) => (
                <List.Item className="propose-mobile-list-item">
                  <article
                    className="propose-mobile-card propose-mobile-proposal-card"
                    onClick={() => goToDetail(proposal)}
                  >
                    <div className="propose-mobile-card-header">
                      <strong className="propose-link">
                        {proposal.propose_code}
                      </strong>
                      <Tag color={statusColor(proposal.status)}>
                        {statusLabel(proposal.status)}
                      </Tag>
                    </div>
                    <div className="propose-mobile-meta">
                      <span>買受人コード</span>
                      <strong>
                        {formatRepresentativeId(proposal.representative_id)}
                      </strong>
                      <span>買受人担当者名</span>
                      <strong>{proposal.recipient_name || "—"}</strong>
                      <span>有効期間</span>
                      <strong>
                        {displayDate(proposal.effective_from)} ～{" "}
                        {displayDate(proposal.effective_to)}
                      </strong>
                      <span>営業入力期限</span>
                      <strong>{displayDateTime(proposal.expires_at)}</strong>
                    </div>
                    {mobileActionButtons(proposal)}
                  </article>
                </List.Item>
              )}
            />
            <MobilePagination
              current={page}
              pageSize={pageSize}
              total={pagination?.total || 0}
              onChange={setPage}
            />
          </div>
        ) : (
          <Table<Propose>
            className="propose-table"
            rowKey="propose_id"
            loading={loading}
            dataSource={proposes}
            tableLayout="fixed"
            scroll={{ x: 1120 }}
            onRow={(proposal) => ({
              onClick: () => goToDetail(proposal),
            })}
            pagination={{
              current: page,
              pageSize,
              total: pagination?.total || 0,
              size: "middle",
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
              showTotal: (total, range) =>
                `${total} 件中 ${range[0]}-${range[1]} を表示`,
              onChange: (nextPage, nextPageSize) => {
                setPage(nextPageSize !== pageSize ? 1 : nextPage);
                setPageSize(nextPageSize);
              },
            }}
            columns={[
              {
                title: "提案書コード",
                dataIndex: "propose_code",
                width: 160,
                render: (value) => (
                  <span className="propose-link">{value}</span>
                ),
              },
              {
                title: "買受人コード",
                dataIndex: "representative_id",
                width: 150,
                render: formatRepresentativeId,
              },
              {
                title: "買受人担当者名",
                dataIndex: "recipient_name",
                width: 150,
                render: (value) => value || "—",
              },
              {
                title: "有効期間",
                width: 220,
                render: (_, row) =>
                  `${displayDate(row.effective_from)} ～ ${displayDate(row.effective_to)}`,
              },
              {
                title: "状態",
                dataIndex: "status",
                width: 120,
                render: (value) => (
                  <Tag color={statusColor(value)}>{statusLabel(value)}</Tag>
                ),
              },
              {
                title: "営業入力期限",
                dataIndex: "expires_at",
                width: 160,
                render: displayDateTime,
              },
              {
                title: "操作",
                key: "actions",
                width: 148,
                fixed: screens.lg ? "right" : undefined,
                align: "center",
                render: (_, proposal) => actionButtons(proposal),
              },
            ]}
          />
        )}
      </Card>
    </main>
  );
};
